import { useQuery } from '@tanstack/react-query';

import { useAuthOptional } from '@/context/AuthContext';
import { useResolvedFeatureFlags } from '@/hooks/useResolvedFeatureFlags';
import type { AccountRecord, AccountRole } from './accountFixture';

/**
 * AR-381 — where the Account center's record comes from (prototype v15).
 *
 * AR-380 built every page against one sample record and said the swap would
 * happen "behind `useAccountRecord`, not the pages". This is that swap: with
 * the migration flag on, `GET /api/account-center` returns the member's real
 * values out of the migrated user model and they are laid over the sample
 * record field by field.
 *
 * A field the migrated model has no source for is ABSENT from the response
 * rather than blank, and is named in `unbound`. That is the difference between
 * "the member has not filled this in" and "nothing in AIVIA holds this yet",
 * and it is why a page never has to invent a column.
 *
 * The role shapes the PAYLOAD, not only the pages (D-ACCT-40). An associate's
 * response carries no plan, price, renewal or commission at all, and no legal
 * entity fields — so a page cannot leak what was never sent, whatever it
 * renders.
 */

/** Only the slices AR-381 binds; every field optional, for the reason above. */
export type AccountRecordPatch = {
  [K in keyof AccountRecord]?: AccountRecord[K] extends readonly unknown[]
    ? AccountRecord[K]
    : Partial<AccountRecord[K]>;
};

/**
 * The facts the pages state but do not edit. Only what AR-381 binds is here —
 * the GEM figures went to the GEM data ticket with the GEM page, and the Sites
 * and preference facts to AR-391, so neither is served from this endpoint.
 */
export interface AccountFacts {
  memberSince?: string;
  showcaseUrl?: string;
  accountStatus?: string;
  teamStatus?: string;
}

/** Who is asking, and what that entitles their payload to carry. */
export interface AccountViewer {
  role: AccountRole;
  isOwner: boolean;
  /** Your Business and Billing are the owner's pages (AC_OWNER_ONLY). */
  canSeeBusiness: boolean;
  canSeeBilling: boolean;
  /**
   * `users.account_status === 'active'`. The user menu's IATA and booking
   * numbers show for an active member only (D-CHROME-CONTACT-01).
   */
  memberActive: boolean;
}

export interface AccountSubscription {
  prod: string;
  plan: string;
  price: number;
  cycle: string;
  status: string;
  desc: string;
}

export interface AccountCharge {
  date: string;
  desc: string;
  amount: number;
  status: string;
  receiptUrl: string | null;
}

/**
 * Everything Stripe answers. Absent for a non-owner, and absent when Stripe
 * is not configured — in which case the reason is in `unbound` and the pages
 * keep the sample rather than stating a price no one is charged.
 */
export interface AccountBilling {
  plan: string;
  price: number;
  cycle: string;
  renewal: string;
  free: boolean;
  subscriptions: AccountSubscription[];
  history: AccountCharge[];
  card: { brand: string; last4: string; exp: string } | null;
}

/** A page field with no source in the shipped schema. */
export interface AccountUnboundField {
  page: string;
  field: string;
  reason: 'schema-add' | 'external' | 'not-configured' | 'other-ticket';
  note: string;
}

export interface AccountCenterPayload {
  viewer: AccountViewer;
  record: AccountRecordPatch;
  facts: AccountFacts;
  billing?: AccountBilling;
  unbound: AccountUnboundField[];
}

/**
 * Lay the migrated values over the record, one slice at a time. A slice the
 * response does not carry is left exactly as it was, and so is a field —
 * `undefined` never overwrites a value, which is what keeps an unbound field
 * showing the sample rather than a blank.
 */
export function mergeAccountRecord(
  base: AccountRecord,
  patch: AccountRecordPatch | undefined,
): AccountRecord {
  if (!patch) return base;
  const next = { ...base } as Record<string, unknown>;
  (Object.keys(patch) as (keyof AccountRecord)[]).forEach((slice) => {
    const incoming = patch[slice];
    if (incoming === undefined) return;
    if (Array.isArray(incoming)) {
      next[slice as string] = incoming;
      return;
    }
    const merged = { ...(base[slice] as object) } as Record<string, unknown>;
    Object.entries(incoming as Record<string, unknown>).forEach(([k, v]) => {
      if (v !== undefined) merged[k] = v;
    });
    next[slice as string] = merged;
  });
  return next as unknown as AccountRecord;
}

/**
 * The migrated record, or nothing.
 *
 * The query only runs while the migration flag resolves on — the same switch
 * that gates the center itself, so there is one switch and not two. With the
 * flag off the endpoint refuses and the pages keep today's behaviour.
 *
 * It does not refetch on focus: a bound field is read-only in this ticket, so
 * a background refetch would quietly replace whatever the member had typed
 * into the card they still have open.
 *
 * The cache is keyed by USER. The local-development bypass replaces the signed
 * in user without a logout, and this payload is one member's own account — a
 * key without the id would serve the last member's name, email and address to
 * the next one for as long as it stayed fresh. The record provider namespaces
 * its localStorage the same way, for the same reason.
 */
export function useAccountSource() {
  const auth = useAuthOptional();
  const userId = auth?.user?.id ?? null;
  const { isEnabled, isLoading: flagsLoading } = useResolvedFeatureFlags();
  // Both, not either: `proMigration` is the parent switch for the whole
  // migration and `proMigrationAccountData` is this ticket's own, so the
  // Account center's front end can ship and be reviewed on the sample record
  // while the binding is still being validated one environment at a time.
  const enabled = isEnabled('proMigration') && isEnabled('proMigrationAccountData');

  const { data, isLoading, error } = useQuery<AccountCenterPayload>({
    queryKey: ['/api/account-center', userId],
    queryFn: async () => {
      const response = await fetch('/api/account-center', { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Failed to load the Account center record (${response.status})`);
      }
      return response.json();
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  return {
    payload: data,
    /** True once the pages are showing the migrated model rather than the sample. */
    isLive: Boolean(data),
    isLoading: flagsLoading || (enabled && isLoading),
    error,
  };
}
